import { useMemo, useState } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Search, UserCog } from 'lucide-react';
import { OrgPortalApi, OrgSchool } from '@/services/org.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { OwnerModal } from '@/components/superadmin/OwnerModal';

/**
 * Org Console equivalent of the Platform's Admin Users screen — same
 * owner-management capability (view/create/reset a school's tenant admin
 * login), scoped to this org's own schools via OrgPortalApi.getOwner/setOwner
 * instead of the superadmin-only SchoolsApi ones. See docs/DEVIATIONS.md.
 */
const columnHelper = createColumnHelper<OrgSchool>();

export function OrgAdminUsersPage() {
  const qc = useQueryClient();
  const [ownerTarget, setOwnerTarget] = useState<OrgSchool | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const dt = useDataTable({ defaultLimit: 20 });

  const { data, isFetching } = useQuery({
    queryKey: ['org-admin-users', dt.params],
    queryFn: () => OrgPortalApi.listSchools(dt.params),
    placeholderData: keepPreviousData,
  });

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'no',
        header: 'No.',
        enableSorting: false,
        cell: (info) => (
          <span className="text-slate-400">{dt.pagination.pageIndex * dt.pagination.pageSize + info.row.index + 1}</span>
        ),
      }),
      columnHelper.accessor('name', {
        id: 'name',
        header: 'School',
        enableSorting: true,
        cell: (info) => (
          <div className="leading-tight">
            <div className="font-medium text-slate-900">{info.getValue()}</div>
            <code className="text-xs text-slate-500">{info.row.original.code}</code>
          </div>
        ),
      }),
      columnHelper.accessor('adminName', {
        id: 'adminName',
        header: 'Admin',
        enableSorting: true,
        cell: (info) => {
          const s = info.row.original;
          return !s.adminName && !s.adminEmail ? (
            <Badge tone="amber">No admin assigned</Badge>
          ) : (
            <div className="text-sm leading-snug">
              <div className="font-medium text-slate-800">{s.adminName ?? '—'}</div>
              <div className="text-slate-500">{s.adminEmail ?? ''}</div>
            </div>
          );
        },
      }),
      columnHelper.accessor('status', {
        id: 'status',
        header: 'School status',
        enableSorting: true,
        cell: (info) => <Badge tone="slate">{info.getValue()}</Badge>,
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: (info) => {
          const s = info.row.original;
          return (
            <button
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
              onClick={() => setOwnerTarget(s)}
            >
              <UserCog className="h-3.5 w-3.5" />
              {s.adminName || s.adminEmail ? 'Manage' : 'Assign admin'}
            </button>
          );
        },
      }),
    ],
    [dt.pagination.pageIndex, dt.pagination.pageSize],
  );

  return (
    <>
      <PageHeader
        title="Admin Users"
        description="Every school in your organization and who administers it. Assign, view, or reset a school admin's login."
      />

      {notice && (
        <div className="mb-4 flex items-center justify-between rounded-md bg-green-50 px-3 py-2 text-sm text-green-800">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} className="text-green-600 hover:text-green-800">
            ✕
          </button>
        </div>
      )}

      <DataTable
        columns={columns}
        result={data}
        state={dt}
        loading={isFetching}
        getRowId={(s) => s.id}
        emptyMessage={dt.params.search ? 'No schools match your search.' : 'No schools yet.'}
        toolbar={
          <div className="relative w-full sm:max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={dt.searchInput}
              onChange={(e) => dt.setSearchInput(e.target.value)}
              placeholder="Search name, code, email…"
              className="pl-9"
            />
          </div>
        }
      />

      {ownerTarget && (
        <OwnerModal
          school={ownerTarget}
          getOwner={OrgPortalApi.getOwner}
          setOwner={OrgPortalApi.setOwner}
          onClose={() => setOwnerTarget(null)}
          onSaved={(msg) => {
            setOwnerTarget(null);
            setNotice(msg);
            qc.invalidateQueries({ queryKey: ['org-admin-users'] });
          }}
        />
      )}
    </>
  );
}
